import type { FieldProps } from '@eliadmin/core/src/components/configurator/configurator-field';
import { useConfiguratorField } from '@eliadmin/core/src/components/configurator/configurator-field';
import { ConfiguratorFieldLabel } from '@eliadmin/core/src/components/configurator/configurator-field-label';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useErrSync } from '@eliadmin/core/src/stores/configurator-store';
import { $do } from '@eliadmin/core/src/utils/misc-utils';
import { parseProformNumber } from '@eliadmin/core/src/utils/proform-utils';
import { useEffect, useMemo, useState } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';

export function ConfigurationRange({
  fieldConfig,
  field,
  isDisabled,
  // internalValueResetCounter = 0,
}: FieldProps<'range'> & {
  isDisabled?: boolean;
  internalValueResetCounter?: number;
}) {
  const { value: strValue, onChange, productConfId } = useConfiguratorField(fieldConfig);
  const value = parseProformNumber(strValue);
  const [internalValue, setInternalValue] = useState<string>(!isNaN(value) ? String(value) : '');
  const debouncedValue = useDebounce(internalValue, 300);

  useEffect(() => {
    if (debouncedValue !== strValue) onChange(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  // useEffect(() => {
  //   if (!isNaN(value) && value !== Number(internalValue)) {
  //     setInternalValue(String(value));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [internalValueResetCounter]);

  if (field.type !== 'range') throw new Error(`Invalid field type ${field.type}`);

  const min =
    field.variant === 'vyska'
      ? productConfId.root.smako_conf.column.vyska_min
      : field.variant === 'sirka'
        ? productConfId.root.smako_conf.column.sirka_min
        : field.variant === 'mnozstvi'
          ? 1
          : 0;

  const max =
    field.variant === 'vyska'
      ? productConfId.root.smako_conf.column.vyska_max
      : field.variant === 'sirka'
        ? productConfId.root.smako_conf.column.sirka_max
        : undefined;

  const isConstrained = Number(min) !== 0 || Number(max) !== 0;

  // const [min, max] = fieldConfig.min_max.split(' ');
  const isInvalid = $do(() => {
    if (!strValue && fieldConfig.must === '1') {
      return true;
    }

    const maxNum = Number(max);
    const minNum = Number(min);

    if (!isConstrained) {
      return false;
    }

    if (isNaN(value)) {
      return true;
    }

    if (!isNaN(maxNum) && value > maxNum) {
      return true;
    }

    if (!isNaN(minNum) && value < minNum) {
      return true;
    }

    return false;
  });

  useErrSync(fieldConfig.column, isInvalid);

  const helperText = useMemo(() => {
    if (field.variant === 'vyska') {
      return `Minimální výška je ${min}, maximální výška je ${max}`;
    }

    if (field.variant === 'sirka') {
      return `Minimální šířka je ${min}, maximální šířka je ${max}`;
    }

    return null;
  }, [min, max, field.variant]);

  return (
    <div className="flex w-full flex-col gap-1">
      <ConfiguratorFieldLabel fieldConfig={fieldConfig} isInvalid={isInvalid} />
      <div className="flex items-center  gap-2">
        <div>
          <Button
            size="icon"
            variant="outline"
            className="h-12 w-12 bg-background"
            disabled={isDisabled || (isConstrained && Number(value) <= Number(min))}
            onClick={() => {
              if (isConstrained && Number(value) <= Number(min)) return;
              onChange(String(Number(value) - 1));
              setInternalValue(String(Number(value) - 1));
            }}
          >
            <LuMinus />
          </Button>
        </div>
        <Input
          variant={'outlined'}
          min={min}
          aria-invalid={isInvalid}
          max={max}
          id={fieldConfig.column}
          disabled={isDisabled}
          type="number"
          className={{ wrapper: 'max-w-[160px]', main: 'max-w-full' }}
          value={internalValue}
          onChange={e => {
            setInternalValue(e.target.value);
          }}
          onBlur={() => {
            if (internalValue === strValue) return;
            onChange(internalValue);
          }}
        />
        <div>
          <Button
            size="icon"
            variant="outline"
            className="h-12 w-12 bg-background"
            disabled={
              isDisabled || (isConstrained && !!Number(max) && Number(value) >= Number(max))
            }
            onClick={() => {
              if (isConstrained && Number(max) && Number(value) >= Number(max)) return;
              onChange(String(Number(value) + 1));
              setInternalValue(String(Number(value) + 1));
            }}
          >
            <LuPlus />
          </Button>
        </div>
      </div>
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}
